import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

const emptyForm = {
  name: "",
  code: "",
  startDate: "",
  endDate: "",
  isActive: true,
};

const toInputDate = (value) => (value ? value.slice(0, 10) : "");

export default function SeasonModal({
  open,
  onClose,
  onSave,
  initialData = null,
}) {
  const [form, setForm] = useState(emptyForm);
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (!open) return;

    setDateError("");
    setForm(
      initialData
        ? {
            name: initialData.name || "",
            code: initialData.code || "",
            startDate: toInputDate(initialData.startDate),
            endDate: toInputDate(initialData.endDate),
            isActive: initialData.isActive ?? true,
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      setDateError("End date cannot be before start date.");
      return;
    }

    onSave({
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
      isActive: form.isActive,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {initialData ? "Edit Season" : "Add Season"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Create fashion seasons and product collections.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 p-8">
            {dateError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {dateError}
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium">
                Season Name *
              </label>
              <input
                value={form.name}
                placeholder="Summer 2026"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Season Code *
              </label>
              <input
                value={form.code}
                placeholder="SUM26"
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                required
              />
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium">
                  Start Date
                </label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      startDate: event.target.value,
                    }))
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">
                  End Date
                </label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      endDate: event.target.value,
                    }))
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Status
              </label>
              <select
                value={form.isActive ? "active" : "inactive"}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isActive: event.target.value === "active",
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl border border-gray-200 px-6"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="h-11 rounded-xl bg-primary-800 px-6 text-white hover:bg-primary-900"
            >
              {initialData ? "Save Changes" : "Create Season"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}